import React, { useState, useMemo } from 'react';
import { Modal, Button, Badge, Form, InputGroup } from 'react-bootstrap';
import { BiBarcode, BiSearch, BiCopy, BiCheck, BiX, BiMobile, BiPurchaseTag } from 'react-icons/bi';
import styled from 'styled-components';

// --- STYLED COMPONENTS ---
const StyledModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    overflow: hidden;
  }
  .modal-header {
    background: #f8fafc;
    border-bottom: 1px solid #e2e8f0;
    padding: 1.5rem;
  }
  .modal-footer {
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
  }
`;

const SerialRow = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.2s ease;
  background: white;

  &:hover {
    background: #f8fafc;
    padding-left: 20px; /* Subtle nudge effect */
  }

  &:last-child {
    border-bottom: none;
  }
`;

const CopyButton = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <button
            onClick={handleCopy}
            className={`btn btn-sm border-0 d-flex align-items-center justify-content-center transition-all ${copied ? 'text-success bg-success-subtle' : 'text-muted hover-bg-light'}`}
            style={{ width: '32px', height: '32px', borderRadius: '8px' }}
            title="Copy Serial"
        >
            {copied ? <BiCheck size={18} /> : <BiCopy size={16} />}
        </button>
    );
};

const SerialInspector = ({ show, onHide, itemName, serials = [] }) => {
    const [searchTerm, setSearchTerm] = useState('');

    // Safe filtering
    const filteredSerials = useMemo(() => {
        if (!Array.isArray(serials)) return [];
        return serials.filter(s => {
            const sn = s.serialNumber || s.serial_number || '';
            return sn.toLowerCase().includes(searchTerm.toLowerCase());
        });
    }, [serials, searchTerm]);

    const handleCopyAll = () => {
        const allText = filteredSerials.map(s => s.serialNumber || s.serial_number).join('\n');
        navigator.clipboard.writeText(allText);
        // Could add a toast here
    };

    return (
        <StyledModal show={show} onHide={onHide} size="md" centered scrollable backdrop="static">

            {/* 1. HEADER: CLEAN & FUNCTIONAL */}
            <Modal.Header>
                <div className="w-100">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center gap-3">
                            <div className="bg-dark text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px' }}>
                                <BiBarcode size={22} />
                            </div>
                            <div>
                                <h6 className="fw-900 mb-0 text-dark" style={{ fontSize: '1.1rem', letterSpacing: '-0.5px' }}>Serial Inspector</h6>
                                <div className="text-dark small fw-bold text-truncate" style={{ maxWidth: '250px' }}>
                                    {itemName || 'Unknown Item'}
                                </div>
                            </div>
                        </div>
                        <button onClick={onHide} className="btn btn-close shadow-none opacity-50"></button>
                    </div>

                    {/* 2. SEARCH BAR EMBEDDED IN HEADER */}
                    <InputGroup className="shadow-sm">
                        <InputGroup.Text className="bg-white border-end-0 text-muted ps-3">
                            <BiSearch size={18} />
                        </InputGroup.Text>
                        <Form.Control
                            placeholder="Search IMEI / Serial..."
                            className="border-start-0 ps-2 shadow-none fw-bold"
                            style={{ fontSize: '0.9rem', height: '45px' }}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <Button variant="light" className="border border-start-0 text-muted" onClick={() => setSearchTerm('')}>
                                <BiX size={20} />
                            </Button>
                        )}
                    </InputGroup>
                </div>
            </Modal.Header>

            {/* 3. BODY: SCROLLABLE LIST */}
            <Modal.Body className="p-0 bg-light" style={{ minHeight: '300px', maxHeight: '500px' }}>
                {filteredSerials.length > 0 ? (
                    <div className="bg-white mx-3 my-3 rounded-4 shadow-sm border overflow-hidden">
                        {filteredSerials.map((s, idx) => {
                            const serial = s.serialNumber || s.serial_number || 'N/A';
                            const condition = s.condition || 'New';
                            const isNew = condition.toLowerCase().includes('new');

                            return (
                                <SerialRow key={idx}>
                                    {/* Index */}
                                    <div className="text-dark fw-900 me-3 text-center" style={{ width: '30px', fontSize: '0.85rem' }}>
                                        {(idx + 1).toString().padStart(2, '0')}
                                    </div>

                                    {/* Icon */}
                                    <div className="me-3 text-muted opacity-50">
                                        <BiMobile size={20} />
                                    </div>

                                    {/* Serial Data */}
                                    <div className="flex-grow-1">
                                        <div className="font-monospace fw-bold text-dark" style={{ fontSize: '0.95rem', letterSpacing: '0.05em' }}>
                                            {serial}
                                        </div>
                                        {/* Optional: Add Batch Number if available */}
                                        {s.batch_number && (
                                            <div className="text-dark fw-bold" style={{ fontSize: '0.8rem' }}>
                                                BATCH: {s.batch_number}
                                            </div>
                                        )}
                                    </div>

                                    {/* Condition Badge */}
                                    <div className="me-3">
                                        <Badge
                                            bg={isNew ? 'success' : 'warning'}
                                            className="px-2 py-1 rounded-pill text-uppercase fw-900 shadow-sm"
                                            style={{ fontSize: '0.75rem', letterSpacing: '0.05em', color: isNew ? 'white' : 'black' }}
                                        >
                                            {condition}
                                        </Badge>
                                    </div>

                                    {/* Action */}
                                    <CopyButton text={serial} />
                                </SerialRow>
                            );
                        })}
                    </div>
                ) : (
                    /* Empty State */
                    <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted py-5">
                        <BiSearch size={48} className="mb-3 opacity-25" />
                        <span className="fw-bold">No Serials Found</span>
                        <span className="small opacity-75">Try a different search term</span>
                    </div>
                )}
            </Modal.Body>

            {/* 4. FOOTER: ACTIONS */}
            <Modal.Footer className="justify-content-between px-4">
                <div className="d-flex align-items-center gap-2">
                    <BiPurchaseTag className="text-dark" />
                    <span className="fw-900 text-dark" style={{ fontSize: '1rem' }}>{filteredSerials.length} Units</span>
                    <span className="text-dark small fw-bold">Displaying</span>
                </div>

                <div className="d-flex gap-2">
                    <Button variant="light" size="sm" className="fw-bold border text-muted" onClick={handleCopyAll}>
                        Copy All
                    </Button>
                    <Button variant="dark" size="sm" className="fw-bold px-4" onClick={onHide}>
                        Done
                    </Button>
                </div>
            </Modal.Footer>
        </StyledModal>
    );
};

export default SerialInspector;